"use client";

import React, { forwardRef, useRef } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { AnimatedBeam } from "@/components/ui/animated-beam";
import { Users } from "lucide-react";

const Circle = forwardRef<HTMLDivElement, { className?: string; children?: React.ReactNode }>(({ className, children }, ref) => {
	return (
		<div
			ref={ref}
			className={cn(
				"relative z-20 flex size-12 items-center justify-center rounded-full border-2 border-border bg-white p-3 shadow-[0_0_20px_-12px_rgba(0,0,0,0.8)]",
				className
			)}
		>
			{children}
		</div>
	);
});

Circle.displayName = "Circle";

export function LMSBeam({ className }: { className?: string }) {
	const containerRef = useRef<HTMLDivElement>(null);
	const div1Ref = useRef<HTMLDivElement>(null);
	const div2Ref = useRef<HTMLDivElement>(null);
	const div3Ref = useRef<HTMLDivElement>(null);
	const div4Ref = useRef<HTMLDivElement>(null);
	const div5Ref = useRef<HTMLDivElement>(null);
	const div6Ref = useRef<HTMLDivElement>(null);
	const div7Ref = useRef<HTMLDivElement>(null);

	return (
		<div
			className={cn(
				"relative flex h-[460px] w-full items-center justify-center overflow-hidden rounded-lg /border bg-background /p-10 /md:shadow-xl",
				className
			)}
			ref={containerRef}
			role="img"
			aria-label="LMS integration diagram showing connections between various learning management systems and StudyStash"
		>
			<div className="flex size-full max-w-lg flex-row items-stretch justify-between gap-10">
				<ul className="flex flex-col justify-center gap-2" role="list" aria-label="Supported learning management systems">
					<li className="list-none">
						<Circle ref={div1Ref} className="size-16">
							<Image
								src="/lms/canvas.png"
								alt="Canvas LMS"
								width={90}
								height={90}
							/>
						</Circle>
					</li>
					<li className="list-none">
						<Circle ref={div2Ref} className="size-16">
							<Image
								src="/lms/blackboard.png"
								alt="Blackboard LMS"
								width={90}
								height={90}
							/>
						</Circle>
					</li>
					<li className="list-none">
						<Circle ref={div3Ref} className="size-16">
							<Image
								src="/lms/moodle.png"
								alt="Moodle LMS"
								width={90}
								height={90}
							/>
						</Circle>
					</li>
					<li className="list-none">
						<Circle ref={div4Ref} className="size-16">
							<Image
								src="/lms/classroom.png"
								alt="Google Classroom"
								width={90}
								height={90}
							/>
						</Circle>
					</li>
					<li className="list-none">
						<Circle ref={div5Ref} className="size-16">
							<Image
								src="/lms/d2l.jpg"
								alt="D2L"
								width={90}
								height={90}
							/>
						</Circle>
					</li>
				</ul>
				<div className="flex flex-col justify-center">
					<Circle
						ref={div6Ref}
						className="size-20"
					>
						<Image
							src="/favicon-32x32.png"
							alt="StudyStash Logo"
							width={40}
							height={40}
						/>
					</Circle>
				</div>
				<div className="flex flex-col justify-center">
					<Circle ref={div7Ref} className="size-16">
						<Users size={32} className="text-gray-500" aria-hidden="true" />
					</Circle>
				</div>
			</div>

			<AnimatedBeam
				containerRef={containerRef}
				fromRef={div1Ref}
				toRef={div6Ref}
			/>
			<AnimatedBeam
				containerRef={containerRef}
				fromRef={div2Ref}
				toRef={div6Ref}
			/>
			<AnimatedBeam
				containerRef={containerRef}
				fromRef={div3Ref}
				toRef={div6Ref}
			/>
			<AnimatedBeam
				containerRef={containerRef}
				fromRef={div4Ref}
				toRef={div6Ref}
			/>
			<AnimatedBeam
				containerRef={containerRef}
				fromRef={div5Ref}
				toRef={div6Ref}
			/>
			<AnimatedBeam
				containerRef={containerRef}
				fromRef={div6Ref}
				toRef={div7Ref}
			/>
		</div>
	);
}
